import React, { useState } from 'react';
import { Modal, Row, Col, Card, Typography, Tag } from 'antd';
import {
  TrophyOutlined,
  GlobalOutlined,
  StarOutlined,
  TeamOutlined,
  BookOutlined,
  HeartOutlined,
  BulbOutlined,
  UserOutlined,
  CheckCircleOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;

// Available sections that can be added to resume
const AVAILABLE_SECTIONS = [
  {
    id: 'awards',
    title: 'Awards',
    description: 'Awards and honors received',
    icon: <TrophyOutlined style={{ fontSize: '24px', color: '#faad14' }} />,
    isPremium: false,
  },
  {
    id: 'languages',
    title: 'Languages',
    description: 'Languages you speak and proficiency levels',
    icon: <GlobalOutlined style={{ fontSize: '24px', color: '#1890ff' }} />,
    isPremium: false,
  },
  {
    id: 'achievements',
    title: 'Achievements',
    description: 'Notable accomplishments and achievements',
    icon: <StarOutlined style={{ fontSize: '24px', color: '#eb2f96' }} />,
    isPremium: false,
  },
  {
    id: 'publications',
    title: 'Publications',
    description: 'Research papers, articles, or books published',
    icon: <BookOutlined style={{ fontSize: '24px', color: '#722ed1' }} />,
    isPremium: false,
  },
  {
    id: 'references',
    title: 'References',
    description: 'Professional references and recommendations',
    icon: <TeamOutlined style={{ fontSize: '24px', color: '#13c2c2' }} />,
    isPremium: false,
  },
  {
    id: 'volunteering',
    title: 'Volunteering',
    description: 'Volunteer work and community involvement',
    icon: <HeartOutlined style={{ fontSize: '24px', color: '#f5222d' }} />,
    isPremium: false,
  },
  {
    id: 'interests',
    title: 'Interests',
    description: 'Personal interests and hobbies',
    icon: <BulbOutlined style={{ fontSize: '24px', color: '#52c41a' }} />,
    isPremium: false,
  },
  {
    id: 'industryExpertise',
    title: 'Industry Expertise',
    description: 'Areas of expertise with proficiency levels',
    icon: <ExperimentOutlined style={{ fontSize: '24px', color: '#9254de' }} />,
    isPremium: false,
  },
  {
    id: 'custom',
    title: 'Custom Section',
    description: 'Create a custom section with your own title',
    icon: <UserOutlined style={{ fontSize: '24px', color: '#8c8c8c' }} />,
    isPremium: false,
  },
];

const AddSectionModal = ({
  visible,
  onClose,
  onSelectSection,
  existingSections = [],
}) => {
  const [hoveredSection, setHoveredSection] = useState(null);

  // Filter out sections that already exist in the resume
  const availableSections = AVAILABLE_SECTIONS.filter(
    (section) => !existingSections.includes(section.id)
  );

  const handleSectionSelect = (section) => {
    if (section.isPremium) {
      // Handle premium section - could show upgrade modal
      return;
    }
    onSelectSection(section.id);
    onClose();
  };

  return (
    <Modal
      title={
        <div style={{ textAlign: 'center' }}>
          <Title level={3} style={{ margin: 0 }}>
            Add a new section
          </Title>
          <Text type="secondary">
            Click on a section to add it to your resume
          </Text>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      centered
      styles={{
        body: {
          padding: '24px',
          maxHeight: '70vh',
          overflowY: 'auto',
        },
      }}
    >
      <Row gutter={[16, 16]}>
        {availableSections.map((section) => {
          const isHovered = hoveredSection === section.id;
          const isDisabled = section.isPremium;

          return (
            <Col xs={24} sm={12} md={8} key={section.id}>
              <Card
                hoverable={!isDisabled}
                onClick={() => handleSectionSelect(section)}
                onMouseEnter={() => setHoveredSection(section.id)}
                onMouseLeave={() => setHoveredSection(null)}
                style={{
                  height: '140px',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  border: isHovered && !isDisabled ? '2px solid #1890ff' : '1px solid #f0f0f0',
                  borderRadius: '12px',
                  transition: 'all 0.3s ease',
                  transform: isHovered && !isDisabled ? 'translateY(-4px)' : 'none',
                  boxShadow: isHovered && !isDisabled
                    ? '0 8px 24px rgba(24, 144, 255, 0.15)'
                    : '0 2px 8px rgba(0, 0, 0, 0.06)',
                  opacity: isDisabled ? 0.6 : 1,
                  position: 'relative',
                  overflow: 'hidden',
                }}
                bodyStyle={{
                  padding: '16px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {section.isPremium && (
                  <Tag
                    color="gold"
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      fontSize: '10px',
                    }}
                  >
                    PREMIUM
                  </Tag>
                )}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                  }}
                >
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '10px',
                      backgroundColor: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {section.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <Text
                      strong
                      style={{
                        fontSize: '14px',
                        display: 'block',
                        marginBottom: '4px',
                      }}
                    >
                      {section.title}
                    </Text>
                    <Text
                      type="secondary"
                      style={{
                        fontSize: '12px',
                        lineHeight: '1.4',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {section.description}
                    </Text>
                  </div>
                </div>
                {existingSections.includes(section.id) && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '8px',
                      right: '8px',
                    }}
                  >
                    <CheckCircleOutlined
                      style={{ color: '#52c41a', fontSize: '16px' }}
                    />
                  </div>
                )}
              </Card>
            </Col>
          );
        })}
      </Row>

      {availableSections.length === 0 && (
        <div
          style={{
            textAlign: 'center',
            padding: '40px',
            color: '#8c8c8c',
          }}
        >
          <CheckCircleOutlined
            style={{ fontSize: '48px', marginBottom: '16px', color: '#52c41a' }}
          />
          <Title level={4} style={{ color: '#8c8c8c' }}>
            All sections added!
          </Title>
          <Text type="secondary">
            You've already added all available sections to your resume.
          </Text>
        </div>
      )}
    </Modal>
  );
};

export default AddSectionModal;
